import { onAdvancingSessionActivity } from "common/signalR";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { AdvancingChatRow } from "./AdvancingChatRow";
import { getAdvancingMessageToastId, useMarkAdvancingMessageSeen } from "./useMarkAdvancingMessageSeen";

// Same as the toast limit, so all of them show at once
const maxToasts = 5;
const maxToastBodyLength = 300;
// The server maximum, so every unread message older than the toasted ones is skipped for good
const fetchCount = 500;

// Each message toasts at most once per page load, so a closed toast stays closed until the next page load
export const useAdvancingMessageToasts = (advancingSessionId: number, incomingMessageType: server.dto.AdvancingMessageTypes) => {
  useEffect(() => {
    const shownMessageIds = new Set<number>();
    const incomingAction: server.dto.AdvancingSessionActions = incomingMessageType === "toAgent" ? "sentMessageToAgent" : "sentMessageFromAgent";

    const showUnreadMessages = () => window.CoreApi.AdvancingSession.PaginatedMessage({ advancingSessionId, isSeen: false, messageType: incomingMessageType, page: 1, perPage: fetchCount }, { toastOnError: true }).then(response => {
      if (response.data.hasErrors) return;

      const newMessages = response.data.data.filter(message => !shownMessageIds.has(message.advancingMessageId));
      newMessages.forEach(message => shownMessageIds.add(message.advancingMessageId));

      newMessages.slice(0, maxToasts).forEach(message => {
        const toastId = getAdvancingMessageToastId(message.advancingMessageId);
        toast.info(({ closeToast }) => <AdvancingMessageToast message={message} closeToast={closeToast} />, {
          toastId,
          autoClose: false,
          theme: "light",
          icon: false,
          closeButton: false,
          className: "advancing-message-toast"
        });
      });
    });

    showUnreadMessages();

    const { removeEvent } = onAdvancingSessionActivity(activity => {
      if (activity.itemId !== advancingSessionId) return;
      if (activity.action.value !== incomingAction) return;
      showUnreadMessages();
    });

    return () => {
      removeEvent();
      toast.clearWaitingQueue();
      shownMessageIds.forEach(advancingMessageId => toast.dismiss(getAdvancingMessageToastId(advancingMessageId)));
    };
  }, []);
};

const AdvancingMessageToast = (props: { message: server.dto.AdvancingMessage, closeToast: () => void }) => {
  const markSeenMutation = useMarkAdvancingMessageSeen(props.message.advancingSessionId);
  const body = props.message.body;

  return <div className="grow1">
    <AdvancingChatRow
      message={{ ...props.message, body: body.length > maxToastBodyLength ? body.slice(0, maxToastBodyLength) + "…" : body }}
      isIncoming
      isMarkingSeen={markSeenMutation.isLoading}
      onMarkSeen={() => {
        if (markSeenMutation.isLoading) return;
        markSeenMutation.mutate(props.message.advancingMessageId);
      }}
      onClose={() => props.closeToast()}
      isToast
    />
  </div>;
};
